/**
 * Theme token resolution + the built-in themes.
 *
 * ## Why this lives in the contract package
 *
 * Colors in the contract are semantic tokens resolved to concrete hex **at
 * render time** (D13), so a theme switch is a re-render — never a model
 * mutation, never a round-trip. That resolution is a pure function of the
 * contract (`Theme` + the dotted-token convention the schema documents), and
 * *every* renderer needs it: the chart engine, the screener grid island, the
 * app shell, and whatever renders next.
 *
 * It used to live in `@qufolio/chart-engine`. That made the screener resolve
 * its colors through the *chart* package — and importing that barrel drags in
 * `scichart`, so a WebView host that only shows a screener (D18) would have had
 * to install a WebGL charting library to learn what `pos` means. The only
 * package all renderers already depend on is this one, and token resolution is
 * as much a function of the contract as `validateWorkspace` is.
 *
 * ## One namespace
 *
 * There is exactly one token namespace, shared by chart and grid. `pos`/`neg`
 * are the up/down pair for BOTH an up candle and a positive screener cell:
 * the two used to carry different names *and* different greens, so the same
 * concept rendered two colors on one screen. `background` is likewise the
 * canvas for the chart plot area and the grid rows alike.
 *
 * Resolution order (D13): literal hex wins; otherwise the active theme's token;
 * otherwise the default theme's token; otherwise undefined (caller decides).
 */

import type { Theme, ThemeTokens } from "./generated/theme.js";

/**
 * **Classic** — the signature blue-grey dark palette. Named without the
 * product (owner, 2026-10-06): one build serves several brands, and a theme
 * called "QuFolio Classic" named the wrong app on all but one.
 *
 * Ported from qvOrderflow `COLORS` + WPF SciChartv4Dark, and deliberately
 * unchanged: it is the look the product already has. Everything below the
 * original token set is new chrome tokens, picked from the hexes the app was
 * already hardcoding so Classic renders exactly as before.
 *
 * Its id is all-zero and stays that way: it is the fallback a workspace with no
 * `themeRef` resolves to, and any saved workspace already pointing at it must
 * keep working.
 */
export const QUFOLIO_CLASSIC_THEME: Theme = {
  id: "00000000000000000000000000",
  name: "Classic",
  mode: "dark",
  tokens: {
    background: "#0e1116",
    surface: "#12151c",
    surfaceAlt: "#12161d",
    border: "#262b36",
    grid: "#20242e",
    axis: "#2a2f3a",
    text: "#e6e9ef",
    textSecondary: "#9aa4b2",
    textMuted: "#6b7684",
    rowAlt: "#141821",
    rowHover: "#1a2029",
    selection: "#2a3242",
    positive: "#26a69a",
    negative: "#ef5350",
    neutral: "#787b86",
    accent: "#4fc3f7",
    accentBlue: "#4fc3f7",
    accentOrange: "#f4c042",
    accentTeal: "#26c6da",
    accentPurple: "#b39ddb",
    warn: "#ffe066",
    // **Six colours of their own, not aliases of the accents.** They were
    // `series1 = accent.blue`, `series2 = accent.orange`, `series3 =
    // accent.purple`, `series4 = positive`, `series5 = negative` — exact
    // duplicates, in every theme, so the indicator picker offered fourteen
    // names that painted nine colours and "Series 4" drew the same line as
    // "Positive". Chosen by maximising the smallest CIE76 ΔE against the eight
    // semantic colours and against each other, then rounded by hand: the worst
    // pair in any theme is now ΔE 14 and most are past 20.
    series1: "#5c7cfa",
    series2: "#9775fa",
    series3: "#f06595",
    series4: "#cbb994",
    series5: "#9ccc65",
    series6: "#3ddc84",
  },
};

/**
 * **Dark** — a true-black, higher-contrast theme.
 *
 * Not a softer Classic: the chart canvas and the grid rows are `#000000`, which
 * is the point (OLED, and a trading desk in a dark room). Because Classic's
 * gridlines (`#20242e`) and axis (`#2a2f3a`) simply vanish against black, they
 * are raised here rather than inherited — the same reason the text ramp is
 * lifted. `pos`/`neg` are the vivid green/red pair, which reads better on black
 * than Classic's muted teal.
 */
export const DARK_THEME: Theme = {
  id: "0000000000000000000000DARK",
  name: "Dark",
  mode: "dark",
  tokens: {
    background: "#000000",
    surface: "#0a0a0a",
    surfaceAlt: "#141414",
    border: "#2c2c2c",
    // Raised well above Classic's #20242e: a gridline that disappears on black
    // is worse than no gridline, because the axis labels then float unanchored.
    grid: "#333333",
    axis: "#4a4a4a",
    text: "#f5f5f5",
    textSecondary: "#a8a8a8",
    textMuted: "#7a7a7a",
    rowAlt: "#0c0c0c",
    rowHover: "#1c1c1c",
    selection: "#2e2e2e",
    positive: "#22c55e",
    negative: "#ef4444",
    neutral: "#8a8a8a",
    accent: "#4dabf7",
    accentBlue: "#4dabf7",
    accentOrange: "#ffa94d",
    accentTeal: "#22d3ee",
    accentPurple: "#b197fc",
    warn: "#ffe066",
    series1: "#7c5cff",
    series2: "#f06595",
    series3: "#ffa8a8",
    series4: "#d8cf8f",
    series5: "#a9e34b",
    series6: "#38d9a9",
  },
};

/**
 * **White** — a genuinely white light theme.
 *
 * `background` is `#ffffff` for the chart canvas and the grid rows alike, not a
 * light grey. The inverse of Dark's problem applies: Classic's colors inverted
 * would leave gridlines and secondary text too pale to read, so the ramp is
 * chosen against white — gridlines light enough not to shout, text and the
 * `pos`/`neg` pair dark enough to pass as body text on white (a `#26a69a` teal
 * or a `#22c55e` green on white is not readable).
 */
export const WHITE_THEME: Theme = {
  // Crockford Base32 has no I/L/O/U, so "white" is spelled WHTE.
  id: "0000000000000000000000WHTE",
  name: "White",
  mode: "light",
  tokens: {
    background: "#ffffff",
    surface: "#ffffff",
    surfaceAlt: "#f1f4f8",
    border: "#d7dce4",
    grid: "#e6eaf0",
    axis: "#aab3c0",
    text: "#10141a",
    textSecondary: "#55606f",
    textMuted: "#7c8797",
    rowAlt: "#f7f9fc",
    rowHover: "#eef3fb",
    selection: "#dbe8fb",
    // Tuned in the app by the owner and promoted here on 2026-09-19: a deeper
    // green and a warmer red, both darker than the Google-ish pair they
    // replace, which were bright enough on white to bloom.
    positive: "#0a6137",
    negative: "#c1251a",
    neutral: "#6b7280",
    accent: "#1a73e8",
    accentBlue: "#1a73e8",
    accentOrange: "#e8710a",
    accentTeal: "#0f8f9e",
    accentPurple: "#7b3fb5",
    warn: "#8a6d00",
    series1: "#2b3a8f",
    series2: "#b5179e",
    series3: "#c2255c",
    series4: "#7a3e2a",
    series5: "#5c7a1e",
    series6: "#37474f",
  },
};

/**
 * **Red White & Blue** — the flag, on white paper.
 *
 * **Blue is up and red is down here, and that is the whole design.** Every other
 * theme paints an up candle green, so this is the one place the up/down pair is
 * not the reflex — but a red-white-and-blue theme with a green candle in it is
 * not the flag, and blue/red is the *better* pair on its own merits: it is the
 * one up/down contrast a red-green colour-blind reader can still see, which
 * green/red never was. The cost to accept is the moment of adjustment for
 * someone switching into it from Classic.
 *
 * **Every colour here was tuned in the app and promoted from the override**, and
 * the candles ended up brighter than the official flag hexes rather than on them.
 * `#053e85` and `#c30909` read 10.3:1 and 6.2:1 on white, against Old Glory Blue
 * and Red at 12.9:1 and 6.7:1 — so the pair went from 1.93:1 to **1.65:1**, a
 * little less separation for a lot more *colour*, which is what a flag is for.
 *
 * **`accent` is the navy too, and this theme is the one exception to the rule
 * that it should not be.** Elsewhere an accent is kept off both candle colours,
 * because a cursor wearing the up colour reads as an up bar — it is what moved
 * Halloween's accent off its pumpkin. Here `#024588` sits ΔE 4.8 from `positive`,
 * which is to say the same navy, and that is the choice: the flag has three
 * colours and the chrome is meant to be one of them, not a fourth blue invented
 * to keep a rule. `accent.blue` and `series1` keep the brighter `#2f7fd1`, so the
 * indicator that arrives first is still not the colour of a candle.
 *
 * The chrome ramp is cooled toward the blue so the white does not read as the
 * White theme with two accents dropped in.
 */
export const RED_WHITE_BLUE_THEME: Theme = {
  id: "00000000000000000000000RWB",
  name: "Red White & Blue",
  mode: "light",
  tokens: {
    background: "#ffffff",
    surface: "#ffffff",
    surfaceAlt: "#eef2f8",
    border: "#c8d3e4",
    grid: "#dfe6f1",
    axis: "#9aa9c2",
    text: "#0a1c33",
    textSecondary: "#3f5878",
    textMuted: "#7286a3",
    rowAlt: "#f5f8fc",
    rowHover: "#e6eefb",
    selection: "#cfe0f7",
    positive: "#053e85",
    negative: "#c30909",
    neutral: "#6b7a90",
    accent: "#024588",
    accentBlue: "#2f7fd1",
    accentOrange: "#b06a12",
    accentTeal: "#0f7b6c",
    accentPurple: "#5b3fa0",
    warn: "#786a00",
    series1: "#5145cd",
    series2: "#b5179e",
    series3: "#8a2f5f",
    series4: "#7a4a1f",
    series5: "#5c7a1e",
    series6: "#1a7a4a",
  },
};

/**
 * **Halloween** — pumpkin and poison on a violet-black night.
 *
 * The canvas is near-black with a violet cast (`#0d0a12`) rather than the true
 * black of Dark: pumpkin orange on pure black is a road sign, and the violet
 * ground takes the edge off it while still passing for night.
 *
 * **Bone up, pumpkin down** — the theme's own two colours doing the job green and
 * red do everywhere else. It is the jack-o'-lantern read directly: a lit face on
 * a dark night. The white is warmed to `#f7f3ea` rather than left at `#ffffff`,
 * because a pure-white candle beside the bone text (`#f2e9dc`) reads as a
 * different material, and both are meant to be the same bone.
 *
 * **The accent moved to the witch purple when the down bar took the orange.** It
 * drives the cursor, the rubber band and the annotation grips, and an accent
 * sharing the down colour makes every crosshair look like a down bar — the same
 * argument that kept it off the red before. `accent.orange` still holds the
 * pumpkin, which is the slot that is *supposed* to.
 *
 * `series1` is the purple for that reason too: a single-plot indicator takes the
 * first series colour, and the first one must not be the colour of a down bar.
 * The pumpkin is still in the palette, one place down.
 */
export const HALLOWEEN_THEME: Theme = {
  id: "0000000000000000000000HWEN",
  name: "Halloween",
  mode: "dark",
  tokens: {
    background: "#0d0a12",
    surface: "#141020",
    surfaceAlt: "#1c1629",
    border: "#332a45",
    grid: "#2e2440",
    axis: "#4a3d63",
    text: "#f2e9dc",
    textSecondary: "#b9a8c9",
    textMuted: "#8a7a9c",
    rowAlt: "#120e1a",
    rowHover: "#1f1830",
    selection: "#3a2d52",
    positive: "#f7f3ea",
    negative: "#ff7518",
    neutral: "#8a7a9c",
    accent: "#a855f7",
    accentBlue: "#5bc0eb",
    accentOrange: "#ffa94d",
    accentTeal: "#2ec4b6",
    accentPurple: "#a855f7",
    warn: "#ffe066",
    series1: "#4dabf7",
    series2: "#f783ac",
    series3: "#e64980",
    series4: "#c9a227",
    series5: "#94d82d",
    series6: "#38d9a9",
  },
};

/**
* **Christmas** — gold up, deep red down, evergreen cursor, on snow.
 *
 * **These values were tuned in the app, not written here first.** An admin edit
 * of a built-in is stored as an override under that built-in's own id, which
 * shadows the shipped palette for that user and nobody else — so this block is
 * the override promoted, which is the step that makes it everyone's and the step
 * that makes *Reset* mean what it should. Anything still only in the store is
 * one person's; anything here is the theme.
 *
 * **Gold up, red down, and the pair separates by brightness.** On the snow
 * ground the gold reads 2.34:1 and the red 8.60:1, which is 3.68:1 between the
 * two candles on top of a wide hue gap — the holly green and pillar-box red
 * this palette started from managed 1.07:1, which is the trap every
 * red-and-green Christmas palette walks into. The cost of the gold is its own
 * contrast: 2.34:1 is fine for a filled candle body and thin for the screener
 * cells that read `positive` as text, which is a trade the owner made with both
 * on screen.
 *
 * **The accent is evergreen, and that is the deliberate exception.** Every
 * other theme keeps the accent off both candle colours, because a cursor
 * wearing the down colour reads as a down bar. Here neither candle is green, so
 * the green is free — and at 9.50:1 it is the most legible thing on the pane
 * after the text itself.
 *
 * **Gold borders, silver gridlines.** One ground for background, surface and
 * surfaceAlt means nothing but a border separates a panel from the chart, so
 * the border is the candles' own gold; the grid is cool against it (1.40:1,
 * furniture rather than palette) and colours the screener's row lines too.
 */
export const CHRISTMAS_THEME: Theme = {
  // Unchanged through every restyle: a saved workspace may already point at it,
  // and a palette may be redesigned but an id may never move.
  id: "0000000000000000000000XMAS",
  name: "Christmas",
  mode: "light",
  tokens: {
    // Snow rather than the cream this shipped with (owner, 2026-09-19, tried in
    // the app first): the warm ground pushed every overlay towards the same
    // festive palette the theme is named for, which is charming for a week and
    // tiring after it. `#fffafa` keeps a trace of warmth without tinting the
    // overlays drawn on it.
    //
    // **All three the same**, so nothing separates a toolbar or a panel from
    // the chart but its border — which is why that border is the gold the up
    // candles use rather than a hairline nobody would see.
    background: "#fffafa",
    surface: "#fffafa",
    surfaceAlt: "#fffafa",
    border: "#c9a227",
    // Silver, and cool on purpose against the gold: the gridlines read as
    // furniture rather than as part of the festive palette. It colours the
    // screener's row lines too.
    grid: "#d4d7db",
    axis: "#c3b087",
    text: "#1c1a14",
    textSecondary: "#4d4636",
    textMuted: "#857c66",
    rowAlt: "#fdf9ee",
    rowHover: "#f7f0e0",
    selection: "#eee2c6",
    // Gold up, deep red down, green accent — the owner tuned the three in the
    // app and chose this brighter gold over the #b88d00 they had been using.
    //
    // It is as metallic as one flat token gets: a sheen needs a gradient, and a
    // candle body is a solid WebGL fill. The cost is contrast — 2.4:1 on white
    // against 3.1:1 — which is fine on a filled bar and thin for the screener
    // cells that read the same token, so it is a deliberate trade rather than
    // an oversight.
    positive: "#c9a227",
    negative: "#950f1a",
    neutral: "#7a7362",
    accent: "#034f1b",
    accentBlue: "#1f5fa8",
    accentOrange: "#c2410c",
    accentTeal: "#0f7b6c",
    accentPurple: "#6b3fa0",
    warn: "#8f6a00",
    series1: "#3730a3",
    series2: "#b5179e",
    series3: "#c2255c",
    series4: "#7d3f57",
    series5: "#5c7a1e",
    series6: "#2f9e44",
  },
};

/**
 * Every theme shipped with the app, in pick-list order.
 *
 * A workspace persists only `themeRef` (D13), so a build-time list is what a
 * reference resolves against. User-authored themes will join this by being
 * looked up ahead of it, not by replacing it.
 *
 * The three neutrals come first and the three themed ones after, because the
 * list is read top-down by someone looking for a working palette rather than for
 * a season — and because Classic must stay first, being the default.
 */
/**
 * **Blue & Orange** — a palette where up and down are told apart by hue that
 * survives every common form of colour vision deficiency.
 *
 * Red and green are the two hues most often confused: deuteranomaly and
 * protanomaly together affect roughly one man in twelve, and both compress
 * exactly the axis every other theme here uses to say *this bar went up*. On
 * those eyes a red candle and a green one differ mainly in lightness, which is
 * the one channel a chart also uses for a dozen other things.
 *
 * **Blue up, orange down**, because blue–orange is the pairing that separates
 * under all three types — it moves along the axis that stays intact rather than
 * the one that collapses. The colours are the Okabe–Ito set, chosen for that
 * property rather than by eye, and the six series colours come from the same
 * eight so no two plots on one chart can merge into each other.
 *
 * **Not "the accessible theme".** It is a working palette that happens to be
 * robust, and naming it for a condition would make choosing it a disclosure.
 * Plenty of people prefer blue and orange on a chart for no medical reason at
 * all, which is the point.
 *
 * **The accent is the reddish purple, not the blue.** It drives the crosshair,
 * the rubber band and the annotation grips, and an accent sharing the *up*
 * colour makes every cursor read as a rising bar — the mirror of the rule
 * Halloween states for the down colour. Purple is in the same eight and is
 * distinguishable from both.
 *
 * **`series1` is the bluish green**, one place away from both candle colours,
 * for Halloween's reason: a single-plot indicator takes the first series colour,
 * and the first one must not be the colour of a candle.
 */
export const BLUE_ORANGE_THEME: Theme = {
  id: "0000000000000000000000BRNG",
  name: "Blue & Orange",
  mode: "dark",
  tokens: {
    background: "#0e1116",
    surface: "#151a21",
    surfaceAlt: "#1c222b",
    border: "#2b333f",
    grid: "#1e2530",
    axis: "#5d6b7d",
    text: "#e8edf4",
    textSecondary: "#a8b4c4",
    textMuted: "#718093",
    rowAlt: "#141a21",
    rowHover: "#1f2732",
    selection: "#25405a",
    // Sky blue up, orange down. The lighter blue rather than Okabe-Ito's
    // #0072B2, which is too dark to read as a lit candle on this ground.
    positive: "#56b4e9",
    negative: "#e69f00",
    neutral: "#8593a5",
    accent: "#cc79a7",
    accentBlue: "#56b4e9",
    accentOrange: "#e69f00",
    accentTeal: "#009e73",
    accentPurple: "#cc79a7",
    warn: "#f0e442",
    series1: "#009e73",
    series2: "#cc79a7",
    series3: "#f0e442",
    series4: "#0072b2",
    series5: "#d55e00",
    series6: "#9ad0f5",
  },
};

export const BUILTIN_THEMES: readonly Theme[] = [
  QUFOLIO_CLASSIC_THEME,
  DARK_THEME,
  WHITE_THEME,
  RED_WHITE_BLUE_THEME,
  BLUE_ORANGE_THEME,
  HALLOWEEN_THEME,
  CHRISTMAS_THEME,
];

/** The theme used when a workspace has no `themeRef` (or an unknown one). */
export const DEFAULT_THEME: Theme = QUFOLIO_CLASSIC_THEME;

/**
 * Resolve a `UserWorkspace.themeRef` to a theme.
 *
 * Falls back to the default rather than throwing: a workspace saved against a
 * theme this build doesn't have must still open, just in the default palette.
 */
export function themeById(id: string | undefined): Theme {
  if (!id) return DEFAULT_THEME;
  return BUILTIN_THEMES.find((t) => t.id === id) ?? DEFAULT_THEME;
}

/** Convert a dotted token ref ("accent.teal") to a ThemeTokens key ("accentTeal"). */
export function tokenRefToKey(ref: string): string {
  const parts = ref.split(".").filter(Boolean);
  return parts
    .map((p, i) => (i === 0 ? p : p.charAt(0).toUpperCase() + p.slice(1)))
    .join("");
}

/** True if `ref` is a literal hex color rather than a token reference. */
export function isHexColor(ref: string): boolean {
  return ref.startsWith("#");
}

/**
 * Named palettes every theme falls back to, by mode — what `palette.<name>.<n>`
 * resolves to when the theme carries no palette of that name (`Theme.palettes`
 * wins when it does, so a palette is retuned as theme data, not code).
 *
 * `guppy` is JAS Guppy's eleven SMAs, 50 to 300, in QuantVitals' colours (owner,
 * 2026-10-03). Light mode is the same sequence with the two that vanish on
 * white — the white and the yellow — darkened.
 */
export const BUILTIN_PALETTES: Readonly<Record<"dark" | "light", Readonly<Record<string, readonly string[]>>>> = {
  dark: {
    guppy: ["#FFFFFF", "#FFB6C1", "#00FFFF", "#008B8B", "#FFFF00", "#FFA500",
            "#FF0000", "#800080", "#FF00FF", "#0000FF", "#000080"],
  },
  light: {
    guppy: ["#555555", "#FFB6C1", "#00FFFF", "#008B8B", "#B8860B", "#FFA500",
            "#FF0000", "#800080", "#FF00FF", "#0000FF", "#000080"],
  },
};

/** `palette.guppy.3` → the theme's `guppy` palette, else the built-in one for its mode. */
function resolvePaletteRef(ref: string, theme: Theme): string | undefined {
  const [, name, at] = ref.split(".");
  const index = Number(at);
  if (!name || !Number.isInteger(index) || index < 0) return undefined;
  const own = theme.palettes?.find((p) => p.name === name)?.colors;
  const builtin = BUILTIN_PALETTES[theme.mode === "light" ? "light" : "dark"][name];
  return own?.[index] ?? builtin?.[index];
}

/**
 * Resolve a color reference (hex, token, or `palette.<name>.<n>`) to a hex string.
 * Returns undefined if a token is unknown in both the active and default theme.
 */
export function resolveColorRef(
  ref: string | undefined,
  theme: Theme = DEFAULT_THEME,
): string | undefined {
  if (!ref) return undefined;
  if (isHexColor(ref)) return ref;
  if (ref.startsWith("palette.")) return resolvePaletteRef(ref, theme);

  const key = tokenRefToKey(ref) as keyof ThemeTokens;
  const active = theme.tokens[key];
  if (typeof active === "string") return active;

  const fallback = DEFAULT_THEME.tokens[key];
  return typeof fallback === "string" ? fallback : undefined;
}

/**
 * The shared chrome vocabulary: CSS custom property → token ref.
 *
 * Component chrome is styled with module-level style objects (which cannot call
 * a hook) across two packages — the app shell and the screener island. Both
 * publish these variables on their root and write `var(--qf-surface)` in their
 * styles, so a theme switch is one inline-style change that cascades, and the
 * two surfaces cannot drift onto different vocabularies.
 *
 * Renderer-specific variables (AG Grid's `--ag-*`, say) are mapped by that
 * renderer; only the shared set lives here.
 */
export const UI_CSS_VARS: Readonly<Record<string, string>> = {
  "--qf-background": "background",
  "--qf-surface": "surface",
  "--qf-surface-alt": "surface.alt",
  "--qf-border": "border",
  "--qf-text": "text",
  "--qf-text-secondary": "text.secondary",
  "--qf-text-muted": "text.muted",
  "--qf-row-alt": "row.alt",
  "--qf-row-hover": "row.hover",
  "--qf-selection": "selection",
  "--qf-accent": "accent",
  // The screener's header marks: blue for a computed column, purple for a
  // band's period (docs/plans/screener-bands.md).
  "--qf-accent-blue": "accent.blue",
  "--qf-accent-purple": "accent.purple",
  "--qf-warn": "warn",
  "--qf-positive": "positive",
  "--qf-negative": "negative",
};

/**
 * {@link UI_CSS_VARS} resolved against a theme, ready to spread into a `style`.
 *
 * Returns a plain record rather than React's `CSSProperties` — this package
 * stays framework-free, and every caller spreads it into a style object anyway.
 */
export function themeCssVariables(theme: Theme): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [cssVar, token] of Object.entries(UI_CSS_VARS)) {
    out[cssVar] = resolveColorRef(token, theme) ?? token;
  }
  return out;
}

/**
 * A resolver bound to one theme, for renderers that take a plain
 * `(token) => css` function (the screener island, so it needs no theme
 * dependency of its own).
 *
 * Unknown tokens pass through unchanged rather than resolving to a fallback
 * color: passing the raw string on gives CSS a chance to make sense of it
 * (`"transparent"`, `"currentColor"`) and makes a typo visible instead of
 * silently painting the wrong swatch.
 */
export function tokenResolverFor(theme: Theme): (token: string) => string {
  return (token: string) => resolveColorRef(token, theme) ?? token;
}
